import {TFunction} from 'i18next';
import {
  DEFAULT_DATE_FORMAT,
  formatFromServerDate,
} from '../../common/util/date';
import {toCamelCase} from '../../common/util/string';
import {getCommentCreateDtText} from '../common';
import {IBanInfo, IComment, ICommentLikeStatus, ICommentType} from '../type';

interface I_Comments {
  commentList: I_Comment[];
}

export interface I_Comment {
  id: number;
  parentCommentId: number;
  best: boolean;
  writer: string;
  writerUserId: string;
  text: string;
  type: 'AUTHOR_MESSAGE' | 'NORMAL' | 'TEMPORARY' | 'ADMIN_DELETE';
  spoiling: boolean;
  withdraw: boolean;
  block: boolean;
  createdDateTime: string;
  likeCount: number;
  dislikeCount: number;
  replyCount: number;
  episodeTitle?: string;
  my: {
    feedback?: 'LIKE' | 'DISLIKE';
    mine: boolean;
  };
}

export function getCommentList({
  t,
  comments,
  isIncludeEpisodeTitle,
}: {
  t: TFunction;
  comments?: I_Comments;
  isIncludeEpisodeTitle?: boolean;
}): IComment[] {
  const commentList = comments?.commentList;
  if (!commentList) return [];

  const now = Date.now();
  return commentList.map<IComment>(comment =>
    getComment({comment, now, isIncludeEpisodeTitle, t})
  );
}

export function getComment({
  comment,
  now,
  isIncludeEpisodeTitle,
  t,
}: {
  comment: I_Comment;
  now: number;
  isIncludeEpisodeTitle?: boolean;
  t: TFunction;
}): IComment {
  return {
    id: comment.id,
    userName: comment.writer,
    userId: comment.writerUserId,
    likeCount: comment.likeCount,
    dislikeCount: comment.dislikeCount,
    replyCount: comment.replyCount,
    content: comment.text,
    isBest: comment.best,
    type: toCamelCase(comment.type) as ICommentType,
    isMine: comment.my?.mine,
    isReply: comment.parentCommentId !== 0,
    isSpolier: comment.spoiling,
    isWithdraw: comment.withdraw,
    isBlock: comment.block,
    createDtText: getCommentCreateDtText({
      createdDateTime: comment.createdDateTime,
      now,
      t,
    }),
    parentId: comment.parentCommentId,
    episodeTitle: isIncludeEpisodeTitle ? comment?.episodeTitle : undefined,
    likeStatus: comment.my?.feedback
      ? (toCamelCase(comment.my?.feedback) as ICommentLikeStatus)
      : 'none',
  };
}

export function getBanInfo(
  data:
    | {
        created: false;
        banInfo: {
          bannedFromDateTime: string;
          bannedToDateTime: string;
          permanentBan: boolean;
        };
      }
    | {created: true}
): IBanInfo | undefined {
  if (data.created) return undefined;

  const {banInfo} = data;
  return {
    isPermanent: banInfo.permanentBan,
    startDt: formatFromServerDate(
      banInfo.bannedFromDateTime,
      DEFAULT_DATE_FORMAT
    ),
    endDt: formatFromServerDate(banInfo.bannedToDateTime, DEFAULT_DATE_FORMAT),
  };
}
